import h from 'hyperscript';

import type { Icon } from 'cadenza/utils/icon/icon';
import { CELL_WRAPPER_CLASS, CELL_WRAPPER_VALUE_CLASS, CELL_WRAPPER_ICON_CLASS } from 'cadenza/result-table/result-table-classes';
import { px } from 'cadenza/utils/px';
import type { PreparedResultTableColumn } from 'cadenza/result-table/prepared-result-table-column';

export function wrapCellContent (cell: HTMLTableCellElement, valueContent?: (Node | string)[]) {
  // Note: The wrapper is always inside the cell and used to support collapsing
  let wrapper = cell.querySelector<HTMLElement>(`.${CELL_WRAPPER_CLASS}`);
  if (!wrapper) { // Only if not yet exists, add it.
    wrapper = h<HTMLElement>(`.${CELL_WRAPPER_CLASS}`,  {
      ...cell.classList.contains('dt-body-right') && { style: { 'justifyContent': 'flex-end' } }
    }, ...valueContent?.length // Just add the wrapper value class in case we have new content
      ? [ h(`span.${CELL_WRAPPER_VALUE_CLASS}.ellipsis`, valueContent) ]
      : []
    );
    cell.replaceChildren(wrapper);
  }
  return wrapper;
}

/**
 * Inserts an icon after the previous cell value or replaces it with it.
 * Includes it in a cadenza link, if one is available.
 *
 * @param cell - The previous HTMLTableCellElement that is manipulated by this function.
 * @param iconToAdd - An icon which should be displayed behind the previous content.
 * @param iconOnly - true, if the previous content is to be replaced by the icon.
 */
export function addIconBehindCellContent (cell: HTMLTableCellElement, iconToAdd: Icon, iconOnly = false) {
  if (cell.querySelector('.d-cadenza-link')) {
    return; // cadenza-link-result-table-plugin.ts handles VisualizationDetails (Highlighting) and Drillthrough itself.
  }
  iconToAdd.classList.add(CELL_WRAPPER_ICON_CLASS);
  cell.querySelectorAll('svg').forEach(s => s.remove()); // Remove previous SVGs.
  wrapCellContent(cell, !iconOnly && cell.textContent && [ cell.textContent ] || []).append(iconToAdd);
}

export function getDataTableElement (scrollBody: HTMLElement) {
  return [ ...scrollBody.getElementsByTagName('table') ]
    .filter((table) => table.id.startsWith('DataTables_Table_'))[0];
}

export function setDataTableMarginTop (scrollHeadElement: HTMLElement, dataTableElement: HTMLTableElement) {
  // Has to be after "addClass('initialized') for header to have an height > 0
  dataTableElement.style.marginTop = px(scrollHeadElement.getBoundingClientRect().height);
}

export function getHeaderRowHasSubTitle (columns: PreparedResultTableColumn[]) {
  return columns.some(col => col.headerSubTitle || col.topNInformation);
}

export function parseCommaSeparateAttribute (attributeValue: string | null): string[] {
  if (attributeValue) {
    return attributeValue.split(',').map(option => option.trim());
  }
  return [];
}
